import { AllActivesService } from '#app/domain/funnel/actives/AllActivesService';

import { apiTags, baseApi } from '../baseApi';

import { loadFunnelActivesClientsPack, type FunnelActivesClientsPack } from './loadFunnelActivesClientsPack';
import { loadFunnelActivesRatingsServiceItems } from './loadFunnelActivesRatingsServiceItems';

export type FunnelActivesInvestGridArg = {
    managerId: string | number;
    atonValuation: number;
};

export type FunnelActivesClientsPackArg = {
    managerId: string | number;
};

const ACTIVES_CACHE_SEC = 3600;

export const funnelActivesApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getFunnelActivesInvestGrid: builder.query<unknown[], FunnelActivesInvestGridArg>({
            async queryFn({ managerId, atonValuation }) {
                try {
                    const raw = await AllActivesService.do(managerId, atonValuation);
                    const data = Array.isArray(raw) ? raw : [];
                    return { data };
                } catch (error: any) {
                    const err = error as { status?: number; responseData?: any; message?: string };
                    return {
                        error: {
                            status: typeof err?.status === 'number' ? err.status : 500,
                            data: err?.responseData ?? err?.message ?? error,
                        },
                    };
                }
            },
            providesTags: (_result, _err, arg) => [
                { type: apiTags.FunnelActives, id: 'LIST' },
                { type: apiTags.FunnelActives, id: `invest-${String(arg.managerId)}-${arg.atonValuation}` },
            ],
            keepUnusedDataFor: ACTIVES_CACHE_SEC,
        }),
        getFunnelActivesClientsPack: builder.query<FunnelActivesClientsPack, FunnelActivesClientsPackArg>({
            async queryFn({ managerId }) {
                try {
                    const data = await loadFunnelActivesClientsPack(managerId);
                    return { data };
                } catch (error: any) {
                    const err = error as { status?: number; responseData?: any; message?: string };
                    return {
                        error: {
                            status: typeof err?.status === 'number' ? err.status : 500,
                            data: err?.responseData ?? err?.message ?? error,
                        },
                    };
                }
            },
            providesTags: (_result, _err, arg) => [
                { type: apiTags.FunnelActives, id: 'LIST' },
                { type: apiTags.FunnelActives, id: `clients-${String(arg.managerId)}` },
            ],
            keepUnusedDataFor: ACTIVES_CACHE_SEC,
        }),
        getFunnelActivesRatingsServiceItems: builder.query<
            Array<Record<string, number | string>>,
            void
        >({
            async queryFn() {
                try {
                    const data = await loadFunnelActivesRatingsServiceItems();
                    return { data };
                } catch (error: unknown) {
                    const err = error as {
                        status?: number;
                        responseData?: unknown;
                        message?: string;
                    };
                    return {
                        error: {
                            status: typeof err?.status === 'number' ? err.status : 500,
                            data: err?.responseData ?? err?.message ?? error,
                        },
                    };
                }
            },
            providesTags: [
                { type: apiTags.FunnelActives, id: 'LIST' },
                { type: apiTags.FunnelActives, id: 'SERVICE_ITEMS' },
            ],
            keepUnusedDataFor: ACTIVES_CACHE_SEC,
        }),
    }),
});

export const {
    useGetFunnelActivesInvestGridQuery,
    useLazyGetFunnelActivesInvestGridQuery,
    useGetFunnelActivesClientsPackQuery,
    useLazyGetFunnelActivesClientsPackQuery,
    useGetFunnelActivesRatingsServiceItemsQuery,
} = funnelActivesApi;

export type { FunnelActivesClientsPack } from './loadFunnelActivesClientsPack';
